
import { doc, updateDoc, increment, arrayUnion, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import i18n from '../i18n/index';
import { syncPublicUserData } from './syncService';

export const XP_ACTIONS = {
  WORKOUT: 50,
  DIET_LOG: 30,
  DAILY_STAMP: 20, // Replaces DAILY_STREAK
  REFERRAL_SUB: 500,
  CHALLENGE_COMPLETE: 200,
  DAILY_LOGIN: 10,
  FEEDBACK: 500
};

// Advanced leveling curve (up to Lvl 100)
export const calculateLevel = (xp: number) => {
  if (xp < 5000) return Math.floor(xp / 1000) + 1; // 1-5
  if (xp < 20000) return Math.floor((xp - 5000) / 3000) + 6; // 6-10
  if (xp < 100000) return Math.floor((xp - 20000) / 8000) + 11; // 11-20
  return Math.floor((xp - 100000) / 15000) + 21; // 21+
};

export const getXPForNextLevel = (level: number) => {
  if (level < 5) return level * 1000;
  if (level < 10) return 5000 + (level - 5) * 3000;
  if (level < 20) return 20000 + (level - 10) * 8000;
  return 100000 + (level - 20) * 15000;
};

export const awardXP = async (userId: string, amount: number, actionName: string) => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Only log in development - production console should be clean
      if (import.meta.env.DEV) {
        console.warn('[GAMIFICATION] User document does not exist:', userId);
      }
      return;
    }
    
    const userData = userSnap.data() as User;
    
    const newXP = userData.xp + amount;
    const newLevel = calculateLevel(newXP);
    const leveledUp = newLevel > userData.level;

    // Milestone Rewards Logic
    const updates: { xp: ReturnType<typeof increment>; level: number } = {
      xp: increment(amount),
      level: newLevel
    };

    if (leveledUp) {
      if (newLevel === 50) {
        // €25 Store Credit via custom claim
        alert(i18n.t('gamification.milestone_50'));
      }
      if (newLevel === 100) {
        alert(i18n.t('gamification.milestone_100'));
      }
    }

    await updateDoc(userRef, updates);
    // Only log in development - production console should be clean
    if (import.meta.env.DEV) {
      console.log(`[GAMIFICATION] Awarded ${amount} XP to user ${userId} for: ${actionName}`);
    }
    
    // CRITICAL: Sync public data after XP/level change
    // Read updated user data and sync
    try {
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        const updatedUser = updatedUserSnap.data() as User;
        // Sync in background (don't await - non-blocking)
        syncPublicUserData(userId, updatedUser).catch(() => {
          // Errors already logged in syncService
        });
      }
    } catch (syncError: any) {
      // Log but don't throw - sync failure shouldn't block XP award
      if (import.meta.env.DEV) {
        console.warn('[GAMIFICATION] Error syncing public data after XP award:', syncError);
      }
    }
    
    return { leveledUp, newLevel, newXP };
  } catch (error: any) {
    console.error('[GAMIFICATION] Error awarding XP:', {
      userId,
      amount,
      actionName,
      error: error.message,
      code: error.code
    });
    // Don't throw - XP award failure shouldn't break the app
    return null;
  }
};

/**
 * DEPRECATED: updateStreak
 * Replaced by calendar stamp system
 * Kept for backward compatibility only
 */
export const updateStreak = async (userId: string) => {
  // This function is deprecated - use calendarService.addDailyStamp instead
  // Keeping for backward compatibility but it does nothing
  // Only log in development - production console should be clean
  if (import.meta.env.DEV) {
    console.warn('[GAMIFICATION] updateStreak is deprecated. Use calendarService.addDailyStamp instead.');
  }
};
